/**
 * A device that holds the key but has no device row, put right on launch
 * (#1694, spec 6.2).
 *
 * ── HOW A DEVICE GETS INTO THAT SHAPE ──────────────────────────────────────
 *
 * Sign out, then sign back in, on a phone whose platform copy survives.
 * `Logout.js` revokes this device's row and then wipes the key record — and
 * the record is where the DEVICE IDENTITY lives, so the id and its key pair go
 * with it. The platform copy is deliberately left behind: it is keyed by
 * member id and belongs to the phone's owner, like a saved password.
 *
 * On the next launch `runHydration` finds no record, reads that copy, and
 * commits `{ keyId, mk, needsRegistration: true }`. The copy holds the key and
 * nothing else, so there is no identity to publish and `localDevice` stays
 * null. The member now holds MK — every note opens, nothing looks wrong — and
 * has NO device row at all.
 *
 * ── WHY THAT IS WORSE THAN IT LOOKS ────────────────────────────────────────
 *
 * A device with no row is not an APPROVED device, so it cannot approve
 * anything. A member with this phone and one other has silently lost their
 * only approver: the second device asks to be approved, finds nobody who can,
 * and lands on the stranded screen — the shape `stranded-state.js` says the
 * enrolment rules make unreachable. Nothing on the working phone hints at it,
 * and the member finds out at the one moment they needed it not to be true.
 *
 * ── WHY NOTHING CAUGHT IT ──────────────────────────────────────────────────
 *
 * `resolveKeylessDevice` already does exactly the right thing — `hasKey &&
 * needsRegistration` takes the signed self-approval path — but only two things
 * call it: the enrolment attempt resume, which needs an attempt row that
 * sign-out has just deleted, and the approval screen, which a member holding a
 * working key has no reason to open. `enrolIfEligible` skips on
 * ALREADY_HAS_KEY. So the one path that fixes it was the one path nobody took.
 *
 * ── SAFE TO DO SILENTLY ────────────────────────────────────────────────────
 *
 * The device is proving possession of MK, which is the same proof the recovery
 * path uses to self-approve. It is not claiming to be a device the member has
 * to vouch for; it IS the member, holding a key only they could have. Asking
 * would put a question in front of someone with no information to answer it,
 * and a declined prompt would leave them in exactly this state.
 */
import { resolveKeylessDevice } from './device-approval';

/**
 * Register this install when it holds the key and has no row, once per member
 * per session.
 *
 * A watcher rather than a call in one page, for the reason the stranded check
 * is one: the shape has to be resolved wherever the member is — reading a
 * lesson, on the Hub, never opening the Devices screen at all.
 *
 * @param {object} args
 * @param {object} args.store the root Vuex store
 * @param {Function} [args.resolve] injected for tests
 * @returns {() => void} stops watching
 */
export function installOrphanedKeyRegistration({ store, resolve = resolveKeylessDevice } = {}) {
  if (!store) throw new TypeError('installOrphanedKeyRegistration needs the root Vuex store');

  /**
   * The member id while this install holds a key it has never registered, 0
   * otherwise — so the watcher fires on the shape AND on a change of member.
   *
   * `isHydrated` guards the other two for the same reason the stranded check
   * needs it: mid-hydration `hasMasterKey` is false and `needsRegistration` is
   * whatever the last member left, and acting on that pair would register
   * against a member who is on their way out.
   */
  const signal = () => {
    if (store.getters['courses-keys/isHydrated'] !== true) return 0;
    if (store.getters['courses-keys/hasMasterKey'] !== true) return 0;
    if (store.state['courses-keys']?.needsRegistration !== true) return 0;
    const memberId = store.getters['courses-keys/memberId'];
    return Number.isInteger(memberId) && memberId > 0 ? memberId : 0;
  };

  // Once per member per session. `resolveKeylessDevice` reads the status and
  // registers; a second run while the first is in flight would spend another
  // challenge from the member's rate limit to be told the same thing.
  const attempted = new Set();
  const check = member => {
    if (!member || attempted.has(member)) return;
    attempted.add(member);
    resolve({ store }).catch(error => {
      // Never fatal, and never surfaced: the member is reading their notes
      // perfectly well. The next launch tries again, and the approval screen
      // is still there if they get to it first.
      console.warn('[CoursesE2EE] This install could not register the key it holds:', error);
    });
  };

  check(signal());
  return store.watch(signal, check);
}
